import {
  UserAgent,
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
} from 'sip.js';
import VoiceAPI from './voiceAPIClient';

const createCallDisconnectedEvent = () => new CustomEvent('call:disconnected');

// Singleton wrapper around sip.js for Zadarma's WSS gateway.
//
// The browser registers a SIP UA against `wss://<tenant-domain>:4443`
// (domain obtained via the backend's `/v1/webrtc/get_key/` + `my.zadarma.com`
// handshake). Inbound calls trigger a UA `invite` event; outbound calls
// instantiate an `Inviter`. The class is stateful because sip.js wants one
// UA per identity for the lifetime of the page; mirroring Twilio's singleton.
class ZadarmaVoiceClient extends EventTarget {
  constructor() {
    super();
    this.userAgent = null;
    this.activeSession = null;
    this.initialized = false;
    this.inboxId = null;
    this.registerer = null;
    this.isResetting = false;
    this.remoteAudio = null;
  }

  // Plays the remote party's audio. sip.js negotiates the media but does not
  // attach the received track to any sink, so without this the agent hears
  // nothing (one-way audio). Called once the session is Established.
  attachRemoteAudio(session) {
    const pc = session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;

    if (!this.remoteAudio) {
      this.remoteAudio = document.createElement('audio');
      this.remoteAudio.autoplay = true;
      this.remoteAudio.hidden = true;
      document.body.appendChild(this.remoteAudio);
    }

    const stream = new MediaStream();
    pc.getReceivers().forEach(receiver => {
      if (receiver.track) stream.addTrack(receiver.track);
    });
    this.remoteAudio.srcObject = stream;
    this.remoteAudio.play().catch(() => {});
  }

  teardownRemoteAudio() {
    if (!this.remoteAudio) return;
    this.remoteAudio.srcObject = null;
    this.remoteAudio.remove();
    this.remoteAudio = null;
  }

  async initializeDevice(inboxId) {
    this.destroyDevice();

    const creds = await VoiceAPI.getToken(inboxId);
    if (!creds || !creds.wss_url || !creds.sip_uri) {
      throw new Error('Invalid Zadarma credentials');
    }

    this.userAgent = new UserAgent({
      uri: UserAgent.makeURI(creds.sip_uri),
      transportOptions: { server: creds.wss_url },
      authorizationUsername: creds.username,
      authorizationPassword: creds.password,
      displayName: creds.username,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionOptions: {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        },
      },
    });

    this.userAgent.delegate = {
      onInvite: invitation => {
        // Auto-accept incoming invites — the agent is "available" by being
        // logged in. Track the invitation itself (it IS the Session); accept()
        // resolves with void, so tracking its result would miss state changes.
        this.trackSession(invitation);
        invitation.accept().catch(() => this.resetActive());
      },
    };

    // Short registration TTL: the Zadarma extension has a small simultaneous-line
    // limit, and a page refresh/unload can't reliably unregister. A 120s expiry
    // lets stale registrations from prior loads clear fast instead of hogging a
    // line for the default 10 minutes ("line limit" rejections otherwise).
    this.registerer = new Registerer(this.userAgent, { expires: 120 });
    // Await transport + registration so an outbound joinClientCall() can place
    // its INVITE against a registered UA (Zadarma rejects INVITEs otherwise).
    await this.userAgent.start();
    await this.waitForRegistered();

    this.initialized = true;
    this.inboxId = inboxId;

    return this.userAgent;
  }

  // Registers and resolves once Registered — or after a timeout (best-effort, so
  // a slow REGISTER doesn't wedge the call flow).
  waitForRegistered(timeoutMs = 8000) {
    return new Promise(resolve => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const timer = setTimeout(done, timeoutMs);
      this.registerer.stateChange.addListener(state => {
        if (state === RegistererState.Registered) {
          clearTimeout(timer);
          done();
        }
      });
      this.registerer.register().catch(done);
    });
  }

  get hasActiveConnection() {
    return this.activeSession !== null;
  }

  setMuted(shouldMute) {
    const sender = this.activeSession?.sessionDescriptionHandler?.peerConnection
      ?.getSenders()
      .find(s => s.track?.kind === 'audio');
    if (sender) sender.track.enabled = !shouldMute;
    return shouldMute;
  }

  endClientCall() {
    const session = this.activeSession;
    if (session) {
      try {
        if (session.state === SessionState.Established) {
          session.bye();
        } else if (
          session.state === SessionState.Initial ||
          session.state === SessionState.Establishing
        ) {
          // Outbound call not yet answered — cancel the INVITE instead of BYE.
          session.cancel();
        }
      } catch (e) {
        // Session may already be terminating/terminated.
      }
    }
    this.resetActive();
  }

  destroyDevice() {
    this.endClientCall();
    if (this.registerer) {
      try {
        this.registerer.unregister();
      } catch (e) {
        // Already unregistered.
      }
    }
    if (this.userAgent) {
      try {
        this.userAgent.stop();
      } catch (e) {
        // UA may already be stopped.
      }
    }
    this.userAgent = null;
    this.registerer = null;
    this.initialized = false;
    this.inboxId = null;
  }

  // Outbound SIP call. `to` is the contact's phone number; Zadarma's PBX
  // bridges this UA (the agent) to the contact leg per the configured scenario.
  async joinClientCall({ to, conversationId, callSid }) {
    if (!this.userAgent || !this.initialized || !to) return null;
    if (this.activeSession) return this.activeSession;

    const targetURI = UserAgent.makeURI(
      `sip:${to}@${this.userAgent.configuration.uri.host}`
    );
    if (!targetURI) return null;

    const inviter = new Inviter(this.userAgent, targetURI, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
      extraHeaders: [
        `X-Chatwoot-Conversation-Id: ${conversationId || ''}`,
        `X-Chatwoot-Call-Sid: ${callSid || ''}`,
      ],
    });

    // Track the Inviter itself (it IS the Session) so the state listener fires
    // — invite() resolves with the request, not the session, so tracking its
    // result would never attach the remote audio (one-way audio).
    this.trackSession(inviter);
    inviter.invite().catch(() => this.resetActive());

    return null;
  }

  trackSession(session) {
    this.activeSession = session;
    session.stateChange.addListener(state => {
      // sip.js SessionState is capitalized ('Established'/'Terminated'); the old
      // lowercase check never fired, so remote audio was never wired and the
      // session never cleaned up on a remote BYE.
      if (state === SessionState.Established) {
        this.attachRemoteAudio(session);
      } else if (state === SessionState.Terminated) {
        this.resetActive();
      }
    });
  }

  resetActive() {
    // Re-entrancy guard: dispatching call:disconnected runs the store's
    // clearActiveCall synchronously, which calls back into endClientCall →
    // resetActive. Without this guard that recurses until the stack overflows.
    if (this.isResetting) return;
    this.isResetting = true;
    this.teardownRemoteAudio();
    this.activeSession = null;
    this.dispatchEvent(createCallDisconnectedEvent());
    this.isResetting = false;
  }
}

export default new ZadarmaVoiceClient();
